// Catches ONLY final chunk-load failures (deploy-window 404s) from its lazy
// subtree, so the failure stays an in-place retry/dismiss state instead of
// reaching AppErrorBoundary. Non-chunk errors re-throw. Retry resets the
// failed imports and re-keys the subtree; an 'online' event retries for free.
import { Component, Fragment, useEffect, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import * as Sentry from '@sentry/react';

import { Button } from '@/components/ui/button';
import { isChunkLoadError, resetFailedChunkImports } from '@/lib/lazyWithRetry';

interface ChunkErrorProps {
  onRetry: () => void;
  onDismiss?: () => void;
}

const ChunkErrorActions = ({ onRetry, onDismiss }: ChunkErrorProps) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2">
      {onDismiss && (
        <Button variant="ghost" size="sm" onClick={onDismiss} data-testid="chunk-boundary-dismiss">
          {t('ChunkBoundary.dismiss')}
        </Button>
      )}
      <Button variant="outline" size="sm" onClick={onRetry} data-testid="chunk-boundary-retry">
        {t('ChunkBoundary.retry')}
      </Button>
    </div>
  );
};

const InlineChunkError = ({ onRetry, onDismiss }: ChunkErrorProps) => {
  const { t } = useTranslation();
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-dashed border-border px-3 py-2"
      data-testid="chunk-boundary-error"
    >
      <p className="text-sm text-muted-foreground">{t('ChunkBoundary.could_not_load')}</p>
      <ChunkErrorActions onRetry={onRetry} onDismiss={onDismiss} />
    </div>
  );
};

const ScrimChunkError = ({ onRetry, onDismiss }: ChunkErrorProps) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (!onDismiss) return undefined;
    // Capture phase: the scrim is top-most, so Escape must not also reach an
    // open Radix dialog underneath and double-close.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onDismiss();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [onDismiss]);

  return (
    <div
      onClick={onDismiss}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      data-testid="chunk-boundary-error"
    >
      <div
        onClick={(event) => event.stopPropagation()}
        className="flex flex-col items-center gap-3 rounded-lg border border-border bg-background p-6 shadow-lg"
      >
        <p className="text-sm text-muted-foreground">{t('ChunkBoundary.could_not_load')}</p>
        <ChunkErrorActions onRetry={onRetry} onDismiss={onDismiss} />
      </div>
    </div>
  );
};

interface ChunkBoundaryProps {
  children: ReactNode;
  /** 'scrim' renders the error over a dialog-style overlay; default is an inline row. */
  variant?: 'inline' | 'scrim';
  /** When set, the error state offers Dismiss (scrim click / Escape included). */
  onDismiss?: () => void;
}

interface ChunkBoundaryState {
  failed: boolean;
  /** Non-chunk error held for re-throw — the nearest page/app boundary owns it. */
  rethrow: Error | null;
  /** Bumped per retry — re-keys the subtree so the fresh lazy mounts. */
  attempt: number;
}

export class ChunkBoundary extends Component<ChunkBoundaryProps, ChunkBoundaryState> {
  state: ChunkBoundaryState = { failed: false, rethrow: null, attempt: 0 };

  static getDerivedStateFromError(error: Error): Partial<ChunkBoundaryState> {
    return isChunkLoadError(error) ? { failed: true } : { rethrow: error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (!isChunkLoadError(error)) return; // re-thrown in render — the outer boundary reports it
    Sentry.captureException(error, {
      tags: { seam: 'chunk-boundary' },
      extra: { componentStack: errorInfo.componentStack },
    });
  }

  componentDidUpdate(_props: ChunkBoundaryProps, prev: ChunkBoundaryState) {
    if (this.state.failed && !prev.failed) window.addEventListener('online', this.handleOnline);
    else if (!this.state.failed && prev.failed)
      window.removeEventListener('online', this.handleOnline);
  }

  componentWillUnmount() {
    window.removeEventListener('online', this.handleOnline);
  }

  handleOnline = () => this.retry();

  retry = () => {
    resetFailedChunkImports(); // a re-mount alone would replay the cached rejection
    this.setState((s) => ({ failed: false, rethrow: null, attempt: s.attempt + 1 }));
  };

  render() {
    const { failed, rethrow, attempt } = this.state;
    if (rethrow) throw rethrow;
    if (failed) {
      const { variant = 'inline', onDismiss } = this.props;
      const ChunkError = variant === 'scrim' ? ScrimChunkError : InlineChunkError;
      return <ChunkError onRetry={this.retry} onDismiss={onDismiss} />;
    }
    return <Fragment key={attempt}>{this.props.children}</Fragment>;
  }
}
